import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2, 
  Circle, 
  ArrowRight, 
  Sparkles, 
  Zap, 
  Building2, 
  Cloud, 
  Users, 
  X, 
  Layers,
  ChevronDown,
  ChevronUp,
  Award
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../AuthContext';
import { useSavedSolicitations } from '../SavedSolicitationsContext';

interface SaaSOnboardingTourProps {
  onNavigate: (tabId: string, subTab?: string) => void;
}

export const SaaSOnboardingTour: React.FC<SaaSOnboardingTourProps> = ({ onNavigate }) => {
  const { vendorProfile } = useAuth();
  const { savedList, addToast } = useSavedSolicitations();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Check saved state from localStorage
  useEffect(() => {
    const dismissed = localStorage.getItem('rfx_onboarding_dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  const hasVendorCodes = Boolean(
    (vendorProfile?.naicsCodes && vendorProfile.naicsCodes.length > 0) ||
    (vendorProfile?.nigpCodes && vendorProfile.nigpCodes.length > 0)
  );

  const hasCompanyDetails = Boolean(
    vendorProfile?.companyName && (vendorProfile?.ein || vendorProfile?.website)
  );

  const hasSavedOpps = Boolean(savedList && savedList.length > 0);

  const steps = [
    {
      id: 'step-profile',
      title: 'Configure Corporate Vendor Profile & NAICS / NIGP Codes',
      description: 'Add your business certifications and industry codes to match relevant state contracts.',
      completed: hasVendorCodes && hasCompanyDetails,
      actionLabel: 'Edit Profile',
      action: () => onNavigate('profile')
    },
    {
      id: 'step-crawler',
      title: 'Run 50-State Daily Crawler Ingestion',
      description: 'Harvest live bids across California, Texas, Florida, New York, and national portals.',
      completed: true,
      actionLabel: 'Launch Ingestion',
      action: () => onNavigate('rfx-hub', 'opportunities')
    },
    {
      id: 'step-draft',
      title: 'Generate Winning AI Proposal Draft with Gemini 3.7 Flash',
      description: 'Analyze an active RFX opportunity to formulate executive summaries and compliance plans.',
      completed: true,
      actionLabel: 'Explore RFX',
      action: () => onNavigate('rfx-hub', 'opportunities')
    },
    {
      id: 'step-workspace',
      title: 'Connect Google Workspace & Cloud Drive Sync',
      description: 'Seamlessly export proposals to Google Drive, Docs, and sync deadlines to Google Calendar.',
      completed: false,
      actionLabel: 'Connect Workspace',
      action: () => onNavigate('workspace')
    },
    {
      id: 'step-team',
      title: 'Invite Team Members & Set Roles (RBAC)',
      description: 'Collaborate with proposal managers, capture drafters, and compliance reviewers.',
      completed: false,
      actionLabel: 'Manage Team',
      action: () => onNavigate('saas-hub')
    }
  ];

  const completedCount = steps.filter(s => s.completed).length;
  const progressPercent = Math.round((completedCount / steps.length) * 100);

  if (isDismissed) return null;

  return (
    <div className="bg-linear-to-r from-indigo-900 via-indigo-850 to-slate-900 text-white rounded-2xl p-5 shadow-lg border border-indigo-700/50 mb-6 relative overflow-hidden">
      {/* Background ambient badge */}
      <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-radial from-indigo-500/10 to-transparent pointer-events-none" />

      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-amber-300 shrink-0">
            <Award className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-wider bg-indigo-500/30 text-indigo-200 px-2 py-0.5 rounded-full border border-indigo-400/30">
                SaaS Onboarding Tour
              </span>
              <span className="text-xs font-bold text-amber-300">{completedCount} of {steps.length} Milestones Complete ({progressPercent}%)</span>
            </div>
            <h2 className="text-base font-black text-white mt-0.5 tracking-tight">
              Launch Your Autonomous Government Bidding Machine
            </h2>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 rounded-lg text-indigo-300 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            title={isCollapsed ? "Expand Checklist" : "Collapse Checklist"}
          >
            {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
          <button
            onClick={() => {
              setIsDismissed(true);
              localStorage.setItem('rfx_onboarding_dismissed', 'true');
            }}
            className="p-1.5 rounded-lg text-indigo-300 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            title="Dismiss Tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-indigo-950/60 h-2 rounded-full mt-3 overflow-hidden border border-indigo-800 relative z-10">
        <div 
          className="bg-linear-to-r from-amber-400 to-emerald-400 h-full rounded-full transition-all duration-500" 
          style={{ width: `${progressPercent}%` }} 
        />
      </div>

      {/* Expanded Step List */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-indigo-800/80 space-y-2.5 relative z-10"
          >
            {steps.map((step) => (
              <div 
                key={step.id} 
                className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/5 gap-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {step.completed ? (
                    <CheckCircle2 className="w-4.5 h-4.5 text-emerald-400 shrink-0" />
                  ) : (
                    <Circle className="w-4.5 h-4.5 text-indigo-400/60 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className={`text-xs font-bold truncate ${step.completed ? 'text-indigo-200 line-through opacity-80' : 'text-white'}`}>
                      {step.title}
                    </p>
                    <p className="text-[10px] text-indigo-300/80 truncate">{step.description}</p>
                  </div>
                </div>

                <button
                  onClick={step.action}
                  className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white rounded-lg text-[11px] font-bold transition-all shrink-0 flex items-center gap-1 cursor-pointer border border-white/10"
                >
                  {step.actionLabel} <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
